import { motion } from "framer-motion";
import { CheckCircle, Award, Heart, Target, GraduationCap, Users } from "lucide-react";

const features = [
  {
    icon: Award,
    title: "Enseignants qualifiés",
    description: "Des professeurs diplômés et expérimentés, passionnés par la transmission du savoir."
  },
  {
    icon: Target,
    title: "Approche personnalisée",
    description: "Un accompagnement adapté au profil et aux objectifs de chaque élève."
  },
  {
    icon: Heart,
    title: "Bienveillance",
    description: "Un environnement positif et encourageant pour développer la confiance en soi."
  },
  {
    icon: CheckCircle,
    title: "Résultats prouvés",
    description: "Une méthode efficace avec des résultats mesurables dès les premières semaines."
  }
];

const stats = [
  { value: "15+", label: "Années d'expérience", icon: GraduationCap },
  { value: "50+", label: "Enseignants", icon: Users },
  { value: "95%", label: "Taux de réussite", icon: Award },
  { value: "5.0/5", label: "Note Google", icon: Heart }
];

export const About = () => {
  return (
    <section
      id="apropos"
      data-testid="about-section"
      className="py-14 md:py-28 relative overflow-hidden bg-white"
    >
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-50 to-transparent" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-blue-600 font-semibold mb-4 tracking-wider uppercase text-sm bg-blue-50 px-4 py-2 rounded-full">
              À Propos
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-blue-900 mb-4 md:mb-6">
              Pourquoi choisir{" "}
              <span className="text-orange-500">POTENTIEL+</span> ?
            </h2>
            <p className="text-base md:text-lg text-blue-800/70 mb-8 leading-relaxed">
              Depuis notre création, nous accompagnons les élèves dans leur parcours scolaire 
              avec une pédagogie innovante et bienveillante. Notre mission est de révéler 
              le potentiel de chaque enfant et de lui donner les clés de la réussite.
            </p>

            {/* Features */}
            <div className="space-y-5">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  data-testid={`about-feature-${index}`}
                  className="flex gap-4 group"
                >
                  <div className="feature-icon w-12 h-12 rounded-xl bg-blue-50 group-hover:bg-blue-600 flex items-center justify-center flex-shrink-0 transition-colors">
                    <feature.icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-blue-700/60 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Image & Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 border-4 border-white">
              <img
                src="https://images.pexels.com/photos/8617843/pexels-photo-8617843.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Réussite scolaire"
                loading="lazy"
                className="w-full h-[240px] sm:h-[300px] md:h-[350px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent" />
              
              {/* Overlay Text */}
              <div className="absolute bottom-5 left-5 right-5 md:bottom-6 md:left-6 md:right-6">
                <p className="text-white text-base sm:text-lg md:text-xl font-semibold">
                  "La réussite de chaque élève est notre priorité"
                </p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  data-testid={`about-stat-${index}`}
                  className="bg-white rounded-2xl p-5 border border-blue-100 shadow-soft text-center card-hover"
                >
                  <stat.icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-blue-700/60">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
